import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "../assets/img/LeftIcon.svg";
import RemoveIcon from "../assets/img/RemoveIcon.svg";
import AddIcon from "../assets/img/AddIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import autotaskLogo from "../assets/img/autotask_logo.png";
import WarningInfo from "../assets/img/warning_info.svg";
import IntegrationTable from "components/IntegrationTable";
import { useIntegration } from "hooks/useIntegration";
import { slugify } from "utils/stringUtils";
import { getConnectorLogo, formatAppName } from "../utils/connectorUtils";
import { appConfigSchemas } from "../utils/constants";
import { normalizeOnePasswordSignInAddress, ONEPASSWORD_APP, ONEPASSWORD_BASE_URL_KEY } from "../utils/onePasswordConfig";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { useSelector } from "react-redux";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";
import M365ConnectionChecksResultsBox from "../components/m365/M365ConnectionChecksResultsBox";
import M365ClientSecretExpiryCaption from "../components/m365/M365ClientSecretExpiryCaption";
import IntegrationApi from "../apis/integrationApi";
import {
  formatM365ClientSecretExpiryChipLabel,
  m365ClientSecretExpiryTone,
  type M365ClientSecretExpiry,
} from "../utils/m365ClientSecretExpiry";
import {
  didM365TokenCheckSucceed,
  isM365ClientSecretUpdated,
  shouldPersistM365ClientSecret,
} from "../utils/m365ConnectionCheckInstructions";
import { CollapsibleSetupGuidePanel } from "../components/integrationGuides/CollapsibleSetupGuidePanel";
import { GWorkspaceManualStepInstructions } from "../components/integrationGuides/GWorkspaceManualStepInstructions";
import GWorkspaceConnectionChecksResultsBox from "../components/gworkspace/GWorkspaceConnectionChecksResultsBox";
import GWorkspaceOAuthScopeChecksResultsBox from "../components/gworkspace/GWorkspaceOAuthScopeChecksResultsBox";
import GWorkspaceOnboardingPanel, { isTerminal as isOnboardingTerminal } from "../components/gworkspace/GWorkspaceOnboardingPanel";
import type { OnboardingRun } from "../apis/gworkspaceOnboardingApi";
import {
  applyGWorkspaceServiceAccountUpload,
  buildGWorkspaceConfig,
  GWORKSPACE_JSON_UPLOAD_KEY,
  parseGWorkspaceServiceAccountJson,
  validateGWorkspaceForm,
} from "../utils/gworkspaceConfig";

const styles = {
  pageContainer: {
    p: "24px 32px",
    background: "#F9FAFB",
    flexGrow: 1,
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: "14px",
    mr: "4px",
  },
  headerInfoContainer: {
    display: "flex",
    alignItems: "center",
  },
  backButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    minHeight: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mr: 1,
    p: 0,
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  headerTitle: {
    fontWeight: 700,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    color: "#000",
  },
  headerSubtitle: {
    color: "#666F8D",
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    fontWeight: 500,
  },
  logo: {
    height: "57px",
  },
  paperContainer: {
    borderRadius: 3,
    overflow: "hidden",
    border: "1px solid #E5E7EB",
    boxShadow: "none",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: "21px 25px 24px",
    backgroundColor: "#FFF",
    cursor: "pointer",
  },
  sectionTitle: {
    fontWeight: 700,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "18px",
    color: "#5A5E73",
  },
  label: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    fontFamily: "var(--theo-font-sans)",
    mb: "6px",
  },
  textFieldWrapper: {
    height: "48px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
  },
  textFieldInput: {
    height: "100%",
    padding: "13px 16px",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "var(--theo-font-sans)",
    color: "#000",
    boxSizing: "border-box",
  },
  expandButton: {
    backgroundColor: "#F3F4F6",
    borderRadius: "50%",
  },
  configContent: {
    p: "24px",
    backgroundColor: "#FFF",
    borderTop: "1px solid #E5E7EB",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 3,
    mb: 3,
  },
  footerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // On the footer rather than on the report above it: the report only exists after a test, so
    // its margin left the buttons against the checklist on every page that had not been tested.
    mt: 3,
  },
  connectionChip: {
    backgroundColor: "#D9FFDD",
    color: "#000",
    fontWeight: 500,
    fontFamily: "var(--theo-font-sans)",
    fontSize: "16px",
    height: "38px",
    flexShrink: 0,
    borderRadius: "6px",
    border: "1px solid #7AF086",
    justifyContent: "flex-start",
  },
  updatedChip: {
    height: 22,
    fontFamily: "var(--theo-font-sans)",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "2px",
    backgroundColor: "#FBF3EA",
    color: "#C17F4A",
    border: "1px solid #E8C9A8",
  },
  secretExpiryChipError: {
    height: 22,
    fontFamily: "var(--theo-font-sans)",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "2px",
    backgroundColor: "#FEE2E2",
    color: "#B91C1C",
    border: "1px solid #FECACA",
  },
  secretExpiryChipWarning: {
    height: 22,
    fontFamily: "var(--theo-font-sans)",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "2px",
    backgroundColor: "#FBF3EA",
    color: "#C17F4A",
    border: "1px solid #E8C9A8",
  },
  secretExpiryChipNeutral: {
    height: 22,
    fontFamily: "var(--theo-font-sans)",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "2px",
    backgroundColor: "#F3F4F6",
    color: "#4B5563",
    border: "1px solid #E5E7EB",
  },
  testButton: {
    mr: 1,
    textTransform: "none",
    backgroundColor: "#705D4A",
    color: "#FFF",
    borderRadius: "6px",
    minWidth: "85px",
    height: "40px",
    padding: "8px 16px",
    gap: "4px",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#5e4d3c",
    },
  },
  nextButton: {
    backgroundColor: "#F5F5F5",
    color: "#232B44",
    textTransform: "none",
    borderRadius: 2,
    minWidth: "85px",
    height: "40px",
    padding: "8px 16px",
    gap: "4px",
    flexShrink: 0,
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
};

const M365_SECRET_EXPIRY_CHIP_SX = {
  error: styles.secretExpiryChipError,
  warning: styles.secretExpiryChipWarning,
  neutral: styles.secretExpiryChipNeutral,
};

function M365ClientSecretExpiryChip({
  label,
  tone,
}: {
  label: string;
  tone: keyof typeof M365_SECRET_EXPIRY_CHIP_SX;
}) {
  return (
    <Chip size="small" label={label} sx={M365_SECRET_EXPIRY_CHIP_SX[tone]} />
  );
}

// Delay between granting M365 API permissions and re-testing them. M365
// admin-consent grants are eventually consistent and can take from a few
// seconds up to an hour to replicate to the Graph API; Microsoft's
// read-after-write guidance is to wait ~5-10s. We use 10s as a practical
// balance so the follow-up test reflects the grant instead of stale state.
const M365_GRANT_PROPAGATION_DELAY_MS = 10_000;
// A Google Workspace run that completed is tested at once and, when that look was not clean,
// once more this long after, by which time the Super Admin role it assigned has propagated (Q127).
const GWORKSPACE_COMPLETION_RETEST_DELAY_MS = 15_000;
const m365ExpiryApi = new IntegrationApi();

const AppConfiguration: React.FC = () => {
  const { appName, customerId } = useParams();
  const navigate = useNavigate();
  const [configOpen, setConfigOpen] = useState(true);
  const [permissionsOpen, setPermissionsOpen] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [m365GrantLoading, setM365GrantLoading] = useState(false);
  const [m365GrantFeedback, setM365GrantFeedback] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [testFooterMessage, setTestFooterMessage] = useState<{
    message: string;
    severity: "warning" | "error";
  } | null>(null);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    applications,
    applicationActions,
    applicationActionsLoading,
    error,
    testResults,
    getApplicationsByCustomer,
    getApplicationActions,
    clearApplicationActions,
    updateApplicationConfig,
    updateActionPermission,
    testApplicationConfiguration,
    testAndEnableM365Actions,
  } = useIntegration();

  const customers = useSelector(
    (state: any) => state.integration?.customers || []
  );
  const customer = customers.find((c: any) => c.id.toString() === customerId);

  useEffect(() => {
    if (customerId) {
      getApplicationsByCustomer(customerId);
    }
    // eslint-disable-next-line
  }, [customerId]);

  const application = useMemo(
    () => applications.find((a: any) => slugify(a.name) === appName),
    [applications, appName]
  );

  useEffect(() => {
    clearApplicationActions();
  }, [application?.id, clearApplicationActions]);

  useEffect(() => {
    if (customerId && application?.id) {
      getApplicationActions(customerId, application.id);
    }
    // eslint-disable-next-line
  }, [customerId, application?.id]);

  const safeActions = Array.isArray(applicationActions)
    ? applicationActions
    : Array.isArray(applicationActions?.rows)
    ? applicationActions.rows
    : [];

  const configSchema = useMemo(() => {
    if (!application?.name) return [];
    const key = slugify(application.name);
    return appConfigSchemas[key] || [];
  }, [application?.name]);

  const configData = useMemo(() => {
    if (application?.customer_config) {
      const configStr = String(application.customer_config).trim();
      if (configStr === '' || configStr === '{}' || configStr === 'null') {
        return {};
      }
      try {
        const parsed = typeof application.customer_config === 'string' 
          ? JSON.parse(application.customer_config)
          : application.customer_config;
        
        if (application?.name === "gworkspace" && parsed.service_account_json) {
          let serviceAccountData = parsed.service_account_json;

          if (typeof serviceAccountData === "string") {
            try {
              serviceAccountData = JSON.parse(serviceAccountData);
            } catch {
              // keep raw string
            }
          }

          if (typeof serviceAccountData === "object" && serviceAccountData !== null) {
            return {
              admin_email: parsed.admin_email || "",
              [GWORKSPACE_JSON_UPLOAD_KEY]: JSON.stringify(serviceAccountData),
            };
          }
        }
        
        return parsed || {};
      } catch {
        return {};
      }
    }
    return {};
  }, [application?.customer_config, application?.name]);

  const formSourceKey = useMemo(() => {
    if (!customerId || application?.id == null) return null;
    try {
      return `${customerId}:${application.id}:${JSON.stringify(configData)}`;
    } catch {
      return `${customerId}:${application.id}`;
    }
  }, [customerId, application?.id, configData]);

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [testSucceededAfterEdit, setTestSucceededAfterEdit] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<Record<string, string>>({});
  /** Bumps when customer or loaded config changes — not application.id alone (catalog id is shared across customers). */
  const [lastFormSourceKey, setLastFormSourceKey] = useState<string | null>(null);
  const [gworkspaceInstructionsExpanded, setGworkspaceInstructionsExpanded] = useState(false);
  /**
   * Google Workspace's guided run starts in the Add-Integration modal, never here (Q132). This
   * page shows the newest run read-only -- its rows and recordings -- and, when the run failed,
   * the manual instructions for the steps it did not do (Q94, Q97).
   */
  const [onboardingRun, setOnboardingRun] = useState<OnboardingRun | null>(null);
  const onboardingAutoTest = useRef<number | null>(null);
  /** True from the moment a run ends until its automatic test, and any second look, is done. */
  const [gworkspaceAutoTesting, setGworkspaceAutoTesting] = useState(false);
  /**
   * A failed run that this page has just rescued by hand.
   *
   * Saving clears the test flag, and the run in hand still predates the stamp the save writes,
   * so without this the checklist would go back to red the instant it was rescued. The next
   * load reads the stamp and needs none of this.
   */
  const [gworkspaceRescued, setGworkspaceRescued] = useState(false);
  const [m365ClientSecretEdited, setM365ClientSecretEdited] = useState(false);
  const [m365SecretExpiry, setM365SecretExpiry] =
    useState<M365ClientSecretExpiry | null>(null);
  const [m365SecretExpiryLoading, setM365SecretExpiryLoading] = useState(false);

  const updateTrackedFormValues = (
    updater: (prev: Record<string, string>) => Record<string, string>
  ) => {
    setTestFooterMessage(null);
    setFormValues((prev) => {
      const newValues = updater(prev);
      const allKeys = new Set([
        ...Object.keys(initialFormValues),
        ...Object.keys(newValues),
      ]);
      const hasChanges = Array.from(allKeys).some(
        (k) => String(newValues[k] || "") !== String(initialFormValues[k] || "")
      );

      setHasUnsavedChanges(hasChanges);
      if (hasChanges) {
        setTestSucceededAfterEdit(false);
      }

      return newValues;
    });
  };

  const handleGWorkspaceServiceAccountUpload = (rawJson: string, fileName: string) => {
    try {
      const { values, fileNames: uploadedFileNames } = applyGWorkspaceServiceAccountUpload(
        rawJson,
        fileName
      );
      updateTrackedFormValues((prev) => ({ ...prev, ...values }));
      setFileNames((prev) => ({ ...prev, ...uploadedFileNames }));
      setAlert({
        open: true,
        message: "Service account JSON loaded.",
        severity: "success",
      });
    } catch (error: any) {
      setAlert({
        open: true,
        message:
          error?.message ||
          "Invalid service account JSON file. Please upload a valid JSON object.",
        severity: "error",
      });
    }
  };

  const validateGWorkspaceFormLocal = () => {
    const newErrors: Record<string, string> = {};
    if (!formValues.admin_email?.trim()) {
      newErrors.admin_email = "Super Admin Email is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (formSourceKey == null || formSourceKey === lastFormSourceKey) return;

    const initialValues = configSchema.reduce((acc, field) => {
      const value = configData[field.key];
      if (value === null || value === undefined) {
        acc[field.key] = "";
      } else if (typeof value === "object") {
        acc[field.key] = JSON.stringify(value);
      } else {
        acc[field.key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>);

    if (application?.name === "gworkspace") {
      const gworkspaceFileNames: Record<string, string> = {};
      if (initialValues[GWORKSPACE_JSON_UPLOAD_KEY]) {
        gworkspaceFileNames[GWORKSPACE_JSON_UPLOAD_KEY] = "JSON configured";
      }
      setFileNames(gworkspaceFileNames);
    } else {
      setFileNames({});
    }

    setFormValues(initialValues);
    setInitialFormValues(initialValues);
    setM365ClientSecretEdited(false);
    if (application?.name !== "gworkspace") {
      setFileNames({});
    }
    setErrors({});
    setHasUnsavedChanges(false);
    setTestSucceededAfterEdit(false);
    setGworkspaceRescued(false);
    setTestFooterMessage(null);
    setLastFormSourceKey(formSourceKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSourceKey, configSchema, application?.name]);

  // Debug: log Save button state and page context
  useEffect(() => {
    const saveDisabled = !testSucceededAfterEdit;
    console.log("[AppConfig] Save button state", {
      customerId,
      applicationId: application?.id,
      applicationName: application?.name,
      hasUnsavedChanges,
      testSucceededAfterEdit,
      saveDisabled,
      hint: saveDisabled ? "Save requires: click Test and test must pass" : "Save enabled",
    });
  }, [customerId, application?.id, application?.name, hasUnsavedChanges, testSucceededAfterEdit]);

  const handleConfigChange = (key: string, value: string) => {
    if (key === "client_secret") {
      setM365ClientSecretEdited(true);
    }
    updateTrackedFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFileChange = (
    key: string,
    file: File | null,
    options?: { readMode?: "base64" | "text" }
  ) => {
    if (!file) {
      handleConfigChange(key, "");
      setFileNames((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value =
        options?.readMode === "text"
          ? String(reader.result || "")
          : ((reader.result as string)?.split(",")[1] ?? "");

      if (
        application?.name === "gworkspace" &&
        key === GWORKSPACE_JSON_UPLOAD_KEY &&
        options?.readMode === "text"
      ) {
        handleGWorkspaceServiceAccountUpload(value, file.name);
        return;
      }

      handleConfigChange(key, value);
      setFileNames((prev) => ({ ...prev, [key]: file.name }));
    };
    if (options?.readMode === "text") {
      reader.readAsText(file, "utf-8");
    } else {
      reader.readAsDataURL(file);
    }
  };

  const handleSaveConfig = async () => {
    console.log("[AppConfig] handleSaveConfig called", { customerId, applicationId: application?.id, applicationName: application?.name, formValuesKeys: Object.keys(formValues) });
    if (!customerId || !application?.id) {
      console.log("[AppConfig] handleSaveConfig early return: missing customerId or application.id");
      return;
    }
    if (isGWorkspace) {
      const gworkspaceErrors = validateGWorkspaceForm(formValues);
      setErrors(gworkspaceErrors);
      if (Object.keys(gworkspaceErrors).length > 0) return;
    }
    let savedFormValues = formValues;
    if (isOnePassword) {
      const address = normalizeOnePasswordSignInAddress(formValues[ONEPASSWORD_BASE_URL_KEY] || "");
      if (!address.ok) {
        setErrors({ [ONEPASSWORD_BASE_URL_KEY]: address.error });
        return;
      }
      savedFormValues = { ...formValues, [ONEPASSWORD_BASE_URL_KEY]: address.value };
    }

    setIsSaving(true);
    try {
      const configToSave: Record<string, unknown> =
        application?.name === "gworkspace"
          ? buildGWorkspaceConfig(formValues)
          : { ...savedFormValues };
      
      console.log("[AppConfig] calling updateApplicationConfig", { customerId, applicationId: application.id, configKeys: Object.keys(configToSave) });
      await updateApplicationConfig(customerId, application.id, configToSave);
      console.log("[AppConfig] updateApplicationConfig completed successfully");

      setFormValues(savedFormValues);
      setInitialFormValues(savedFormValues);
      setHasUnsavedChanges(false);
      if (onboardingFailedOpen) setGworkspaceRescued(true);
      setTestSucceededAfterEdit(false);

      setAlert({
        open: true,
        message: "Configuration saved successfully!",
        severity: "success",
      });
      
      setTimeout(() => {
        if (customerId) {
          try {
            getApplicationsByCustomer(customerId);
          } catch {
          }
        }
      }, 100);
    } catch (error) {
      setAlert({
        open: true,
        message: "Failed to save configuration. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  /** Returns whether every check came back clean, which is what decides a second look. */
  const handleTestConnection = async (): Promise<boolean> => {
    if (!customerId || !application?.id) return false;
    if (isGWorkspace && !validateGWorkspaceFormLocal()) {
      return false;
    }

    setM365GrantFeedback(null);
    setTestFooterMessage(null);

    const hasConfigValues = Object.values(formValues).some(
      (value) => value && value.trim() !== ""
    );

    if (!hasConfigValues) {
      setTestFooterMessage({
        message:
          "No configuration values to test. Please fill in the required fields first.",
        severity: "warning",
      });
      return false;
    }

    setIsTesting(true);

    try {
      const pastedKey =
        isGWorkspace &&
        formValues[GWORKSPACE_JSON_UPLOAD_KEY]?.trim() &&
        formValues[GWORKSPACE_JSON_UPLOAD_KEY] !== initialFormValues[GWORKSPACE_JSON_UPLOAD_KEY]
          ? parseGWorkspaceServiceAccountJson(formValues[GWORKSPACE_JSON_UPLOAD_KEY])
          : null;
      const configToTest: Record<string, unknown> =
        application?.name === "gworkspace"
          ? { admin_email: formValues.admin_email || "", ...(pastedKey ? { service_account_json: pastedKey } : {}) }
          : { ...formValues };
      
      const result = await testApplicationConfiguration(
        customerId,
        application.name,
        configToTest
      );

      const ok = result?.success !== false;
      const tokenOk = isM365 ? didM365TokenCheckSucceed(result) : ok;
      let secretPersisted = false;
      let persistError: string | null = null;

      if (
        isM365 &&
        shouldPersistM365ClientSecret({
          previousSecret: initialFormValues.client_secret,
          nextSecret: formValues.client_secret,
          testResult: result,
        })
      ) {
        try {
          await updateApplicationConfig(customerId, application.id, {
            ...formValues,
          });
          setInitialFormValues(formValues);
          setHasUnsavedChanges(false);
          secretPersisted = true;
        } catch (error: unknown) {
          persistError = getApiErrorMessage(
            error,
            "Client secret was validated but could not be saved. Click Save to retry."
          );
        }
      }

      setTestSucceededAfterEdit(secretPersisted ? false : tokenOk);
      if (isM365 && result?.client_secret_expiry) {
        setM365SecretExpiry(result.client_secret_expiry);
      }

      const persistNote = persistError
        ? ` ${persistError}`
        : secretPersisted
          ? " New client secret saved."
          : "";
      setAlert({
        open: true,
        message: `${result?.message || "Connection test successful!"}${persistNote}`,
        severity: persistError
          ? "error"
          : ok
            ? result?.has_warnings
              ? "warning"
              : "success"
            : "warning",
      });
      return ok && !result?.has_warnings && !persistError;
    } catch (error: unknown) {
      setTestSucceededAfterEdit(false);
      setAlert({
        open: true,
        message: getApiErrorMessage(
          error,
          "Connection test failed. Please check your configuration."
        ),
        severity: "error",
      });
      return false;
    } finally {
      setIsTesting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const isGWorkspace = application?.name === "gworkspace";
  // 1Password has nothing to test the sign-in address against: Save opens on any change.
  const isOnePassword = application?.name === ONEPASSWORD_APP;
  const isM365 = Boolean(
    application?.name && slugify(application.name) === "m365"
  );
  const m365ReadonlyKeys = new Set(["tenant_id", "client_id"]);
  const m365ClientSecretUpdated =
    isM365 &&
    m365ClientSecretEdited &&
    isM365ClientSecretUpdated(
      initialFormValues.client_secret,
      formValues.client_secret
    );
  const m365SecretExpiryChipLabel = m365ClientSecretUpdated
    ? null
    : formatM365ClientSecretExpiryChipLabel(m365SecretExpiry);
  const m365SecretExpiryChipTone = m365ClientSecretExpiryTone(m365SecretExpiry);
  // Google Workspace shows both fields here as every other integration does (Q138).
  const visibleConfigFields = configSchema;

  const m365TestResult = useMemo(() => {
    if (!isM365 || !customerId || !application?.name) return null;
    const key = `${customerId}:${application.name}`;
    return testResults[key] ?? null;
  }, [isM365, customerId, application?.name, testResults]);

  const gworkspaceTestResult = useMemo(() => {
    if (!isGWorkspace || !customerId || !application?.name) return null;
    const key = `${customerId}:${application.name}`;
    return testResults[key] ?? null;
  }, [isGWorkspace, customerId, application?.name, testResults]);

  /** The Test button is busy for its own run and for the automatic one after a completed run. */
  const testBusy = isTesting || gworkspaceAutoTesting;
  /** A run still going locks the buttons: nothing here may change what the run is writing. */
  const onboardingRunning = Boolean(isGWorkspace && onboardingRun && !isOnboardingTerminal(onboardingRun));
  /** A failed run the technician has not yet finished by hand: red rows, instructions, [Save]. */
  const onboardingFailedOpen = Boolean(isGWorkspace && onboardingRun?.status === "failed" && !onboardingRun?.manually_completed_at);
  /**
   * Whether this customer is already set up, read from the stored configuration rather than the
   * form: the key that is saved is what says so, not what the technician has typed (Q95).
   */
  const gworkspaceConfigured = Boolean(isGWorkspace && initialFormValues[GWORKSPACE_JSON_UPLOAD_KEY]);
  /**
   * Which manual steps to show: the ones a failed run did not do, or every step for a customer
   * with nothing set up (Q97). A customer who is already configured is shown none -- a by-hand
   * guide for steps that are plainly done is noise, and the fallback was written for a customer
   * with no configuration, not merely no run.
   */
  const gworkspaceGuideSteps: string[] | null = !isGWorkspace
    ? null
    : onboardingFailedOpen
      ? (onboardingRun?.steps ?? []).filter((step) => step.state !== "done").map((step) => step.name)
      : onboardingRun || gworkspaceConfigured
        ? null
        : [];

  useEffect(() => {
    if (onboardingFailedOpen) setGworkspaceInstructionsExpanded(true);
  }, [onboardingFailedOpen]);

  const handleOnboardingConfigurationSaved = () => {
    if (customerId) {
      try {
        getApplicationsByCustomer(customerId);
      } catch {
        // The list refresh is a convenience; the run has already written the row.
      }
    }
  };

  // All nine steps went green while this page was watching: the existing [Test] runs at once,
  // and again shortly after when the first look was not clean -- which covers the Super Admin
  // role still propagating, the one failure waiting actually fixes (Q120, Q127). The button is
  // busy across both. A failed run runs nothing; the technician tests after finishing by hand,
  // and nothing at all runs on a page visit.
  const handleOnboardingFinished = (run: OnboardingRun) => {
    if (run.status !== "completed" || !run.configuration_saved) return;
    if (onboardingAutoTest.current != null) window.clearTimeout(onboardingAutoTest.current);
    setGworkspaceAutoTesting(true);
    void handleTestConnection()
      .then((clean) => {
        if (clean) {
          setGworkspaceAutoTesting(false);
          return;
        }
        onboardingAutoTest.current = window.setTimeout(() => {
          onboardingAutoTest.current = null;
          void handleTestConnection().finally(() => setGworkspaceAutoTesting(false));
        }, GWORKSPACE_COMPLETION_RETEST_DELAY_MS);
      })
      .catch(() => setGworkspaceAutoTesting(false));
  };

  useEffect(
    () => () => {
      if (onboardingAutoTest.current != null) window.clearTimeout(onboardingAutoTest.current);
    },
    []
  );

  useEffect(() => {
    if (!isM365 || !customerId) {
      setM365SecretExpiry(null);
      return;
    }
    let cancelled = false;
    setM365SecretExpiryLoading(true);
    m365ExpiryApi
      .getM365ClientSecretExpiry(customerId)
      .then((data) => {
        if (!cancelled) {
          setM365SecretExpiry((data || null) as M365ClientSecretExpiry | null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setM365SecretExpiry({
            available: false,
            reason: "graph_error",
            message: "Could not read client secret expiry from Entra ID.",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setM365SecretExpiryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isM365, customerId, application?.id]);

  const handleGrantM365ApiPermissions = async () => {
    if (!customerId || !application?.name) return;
    setM365GrantLoading(true);
    setM365GrantFeedback(null);
    let grantSucceeded = false;
    try {
      const res = (await testAndEnableM365Actions(customerId)) as {
        message?: string;
        error?: string;
      };
      if (res?.error) {
        setM365GrantFeedback({ success: false, message: res.error });
        setAlert({
          open: true,
          message: res.error,
          severity: "error",
        });
      } else {
        grantSucceeded = true;
        const msg =
          res?.message || "M365 permissions granted successfully.";
        setM365GrantFeedback({ success: true, message: msg });
        setAlert({
          open: true,
          message: msg,
          severity: "success",
        });
      }
    } catch (err: unknown) {
      const msg = getApiErrorMessage(
        err,
        "Failed to grant M365 permissions."
      );
      setM365GrantFeedback({ success: false, message: msg });
      setAlert({
        open: true,
        message: msg,
        severity: "error",
      });
    } finally {
      setM365GrantLoading(false);
    }

    // Same as "Test M365 Credentials and Permissions": refresh checks + connection state (no extra snackbar).
    const hasConfigValues = Object.values(formValues).some(
      (value) => value && value.trim() !== ""
    );
    if (hasConfigValues) {
      setIsTesting(true);
      // M365 admin-consent grants are eventually consistent: it can take from a
      // few seconds up to an hour for newly granted permissions/roles to
      // replicate to the Graph API. Testing immediately reports false negatives
      // (permissions/roles "not granted") because the grant hasn't propagated.
      // Wait a short, practical window before re-testing so the result reflects
      // the grant rather than stale state. Microsoft's read-after-write guidance
      // for eventual consistency is to wait ~5-10s; we use 10s as a balance
      // between accuracy and UI responsiveness.
      if (grantSucceeded) {
        await new Promise((resolve) =>
          setTimeout(resolve, M365_GRANT_PROPAGATION_DELAY_MS)
        );
      }
      try {
        const result = await testApplicationConfiguration(
          customerId,
          application.name,
          { ...formValues }
        );
        const ok = result?.success !== false;
        setTestSucceededAfterEdit(ok);
        if (result?.client_secret_expiry) {
          setM365SecretExpiry(result.client_secret_expiry);
        }
      } catch {
        setTestSucceededAfterEdit(false);
      } finally {
        setIsTesting(false);
      }
    }
  };

  const connectionStatus = useMemo(() => {
    // Nothing tests a 1Password sign-in address, so there is no status to show either.
    if (isOnePassword) {
      return { status: "not_testable", label: "", color: "", show: false };
    }
    const compositeKey =
      customerId && application?.name
        ? `${customerId}:${application.name}`
        : null;
    const currentTestResult = compositeKey ? testResults[compositeKey] : null;

    if (!currentTestResult) {
      return {
        status: "not_tested",
        label: "Not Tested",
        color: "#6B7280", // Gray
        show: true,
      };
    }

    if (currentTestResult.success) {
      return {
        status: "connected",
        label: currentTestResult.message || "Connection Successful",
        color: "#45BE51", // Green
        show: true,
      };
    } else {
      return {
        status: "failed",
        label: currentTestResult.message || "Connection Failed",
        color: "#FF6B6B", // Red
        show: true,
      };
    }
  }, [testResults, customerId, application?.name, isOnePassword]);

  const handlePermissionUpdate = async (params: {
    customerId: string;
    applicationId: string;
    actionName: string;
    permission: "auto" | "approval" | "off";
  }) => {
    try {
      await updateActionPermission(params);
      if (customerId && application?.id) {
        getApplicationActions(customerId, application.id);
      }
      setAlert({
        open: true,
        message: "Permission updated!",
        severity: "success",
      });
    } catch (error) {
      setAlert({
        open: true,
        message: "Failed to update permission.",
        severity: "error",
      });
      throw error;
    }
  };

  const supportSnapshot = useMemo(
    () => ({
      surface: "application_configuration" as const,
      title: application?.name
        ? `${formatAppName(application.name)} application configuration`
        : "Application configuration",
      configuration: {
        customer_id: customerId || "",
        customer_name: customer?.name || "",
        application: application?.name || "",
        enabled: Boolean(application?.enabled),
        has_unsaved_changes: hasUnsavedChanges,
        test_succeeded_after_edit: testSucceededAfterEdit,
        requires_successful_test_before_save: !isOnePassword,
        validation_errors: errors,
        connection_status: connectionStatus.status,
        connection_message: connectionStatus.label,
        alert: alert.open ? { severity: alert.severity, message: alert.message } : null,
        form: formValues,
        m365_client_secret_edited: m365ClientSecretEdited,
        gworkspace_onboarding: onboardingRun
          ? {
              status: onboardingRun.status,
              current_step: onboardingRun.current_step,
              configuration_saved: onboardingRun.configuration_saved,
              failure_reason: onboardingRun.failure?.reason || "",
              failure_message: onboardingRun.failure?.message || "",
              manually_completed: Boolean(onboardingRun.manually_completed_at),
            }
          : null,
        permissions_loading: applicationActionsLoading,
        permissions: safeActions.slice(0, 40).map((action: { name?: string; permission?: string }) => ({
          name: action.name,
          permission: action.permission || "",
        })),
      },
    }),
    [
      application?.name,
      application?.enabled,
      customerId,
      customer?.name,
      hasUnsavedChanges,
      testSucceededAfterEdit,
      isOnePassword,
      errors,
      connectionStatus.status,
      connectionStatus.label,
      alert,
      formValues,
      m365ClientSecretEdited,
      onboardingRun,
      applicationActionsLoading,
      safeActions,
    ]
  );
  usePublishConfigurationSupport("application-configuration", supportSnapshot, 30);

  return (
    <Box sx={styles.pageContainer}>
      {/* Header */}
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerInfoContainer}>
          <IconButton onClick={() => navigate(-1)} sx={styles.backButton}>
            <img
              src={ArrowBackIcon}
              alt="Arrow Back"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
          <Box>
            <Typography sx={styles.headerTitle}>
              {formatAppName(application?.name || "")} Configuration
            </Typography>
            <Typography sx={styles.headerSubtitle}>
              {customer?.name || ""}
            </Typography>
          </Box>
        </Box>
        <img
          src={getConnectorLogo(application?.name || "")}
          alt={formatAppName(application?.name || "")}
          style={styles.logo}
        />
      </Box>

      {/* Configuration Section */}
      <Paper sx={{ ...styles.paperContainer, mb: 2 }}>
        <Box
          sx={styles.sectionHeader}
          onClick={() => setConfigOpen(!configOpen)}
        >
          <Typography sx={styles.sectionTitle}>Configuration</Typography>
          <IconButton size="small" sx={styles.expandButton}>
            {configOpen ? (
              <img src={RemoveIcon} alt="Remove" />
            ) : (
              <img src={AddIcon} alt="Add" />
            )}
          </IconButton>
        </Box>

        {configOpen && (
          <Box sx={styles.configContent}>
            <Box sx={styles.gridContainer}>
              {visibleConfigFields.length > 0 ? (
                visibleConfigFields.map((field) =>
                  field.type === "file" ? (
                    <Box
                      key={field.key}
                      sx={{
                        gridColumn:
                          field.key === GWORKSPACE_JSON_UPLOAD_KEY ? "1 / -1" : "span 1",
                      }}
                    >
                      <Typography sx={styles.label}>{field.label}</Typography>
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          backgroundColor: "#FFF",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            variant="outlined"
                            component="label"
                            disabled={onboardingRunning}
                            sx={{
                              fontFamily: "var(--theo-font-sans)",
                              textTransform: "none",
                              borderColor: "#705d4a",
                              color: "#705d4a",
                              borderRadius: "6px",
                              px: 1.5,
                              minHeight: 40,
                              "&:hover": {
                                borderColor: "#5e4d3c",
                                backgroundColor: "#faf7f4",
                              },
                            }}
                          >
                            {field.uploadLabel || "Choose file"}
                            <input
                              type="file"
                              hidden
                              accept={field.accept || ".pfx"}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                handleFileChange(field.key, f ?? null, {
                                  readMode: field.fileReadMode,
                                });
                                e.target.value = "";
                              }}
                            />
                          </Button>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              ml: "auto",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "var(--theo-font-sans)",
                                fontSize: 13,
                                color: formValues[field.key] ? "#2E7D32" : "#666F8D",
                                fontWeight: 500,
                              }}
                            >
                              {fileNames[field.key] ||
                                (formValues[field.key]
                                  ? field.configuredLabel || "File configured"
                                  : "No file selected")}
                            </Typography>
                            {formValues[field.key] && (
                              <Button
                                size="small"
                                onClick={() =>
                                  handleFileChange(field.key, null, {
                                    readMode: field.fileReadMode,
                                  })
                                }
                                sx={{
                                  fontFamily: "var(--theo-font-sans)",
                                  textTransform: "none",
                                  color: "#d32f2f",
                                  minWidth: 0,
                                  px: 0.5,
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </Box>
                        </Box>
                        {field.helpText && (
                          <Typography
                            sx={{
                              mt: 1,
                              color: "#666F8D",
                              fontSize: "12px",
                              lineHeight: 1.4,
                              fontFamily: "var(--theo-font-sans)",
                            }}
                          >
                            {field.helpText}
                          </Typography>
                        )}
                        {errors[field.key] && (
                          <Typography
                            sx={{
                              mt: 1,
                              color: "#d32f2f",
                              fontSize: "12px",
                              lineHeight: 1.4,
                              fontFamily: "var(--theo-font-sans)",
                            }}
                          >
                            {errors[field.key]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    // The grid collapses empty auto-fit tracks, so a lone field fills the row.
                    // Google Workspace has two, and the JSON box spans every track -- which
                    // leaves nothing to collapse and squeezes the address into one 200px track.
                    // Both of its fields span the row, as they do in the Add-Integration modal.
                    <Box key={field.key} sx={isGWorkspace ? { gridColumn: "1 / -1" } : undefined}>
                      <Typography
                        sx={{
                          ...styles.label,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {field.label}
                        {isM365 && m365ReadonlyKeys.has(field.key) ? (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#6B7280",
                            }}
                          >
                            (read-only)
                          </Typography>
                        ) : null}
                        {isM365 &&
                        field.key === "client_secret" &&
                        m365ClientSecretUpdated ? (
                          <Chip
                            size="small"
                            label="Updated"
                            sx={styles.updatedChip}
                          />
                        ) : null}
                      </Typography>
                      <TextField
                        disabled={onboardingRunning}
                        sx={
                          field.type === "textarea"
                            ? {
                                "& .MuiInputBase-root": {
                                  borderRadius: "8px",
                                  border: "1px solid #E2E8F0",
                                  px: 2,
                                  py: 1.5,
                                  fontFamily: "var(--theo-font-sans)",
                                  alignItems: "flex-start",
                                  ...(isM365 &&
                                    m365ReadonlyKeys.has(field.key) && {
                                      backgroundColor: "#F3F4F6",
                                    }),
                                },
                                "& .MuiInputBase-root:hover": {
                                  borderColor: "#705D4A",
                                },
                              }
                            : {
                                ...styles.textFieldWrapper,
                                ...(isM365 &&
                                  m365ReadonlyKeys.has(field.key) && {
                                    backgroundColor: "#F3F4F6",
                                    "& .MuiInputBase-input": {
                                      cursor: "default",
                                    },
                                  }),
                                ...(isM365 &&
                                  field.key === "client_secret" && {
                                    overflow: "visible",
                                  }),
                                ...(isM365 &&
                                  field.key === "client_secret" &&
                                  m365ClientSecretUpdated && {
                                    borderColor: "#C17F4A",
                                    backgroundColor: "#FBF3EA",
                                  }),
                              }
                        }
                        fullWidth
                        placeholder={field.placeholder}
                        value={formValues[field.key] || ""}
                        onChange={(e) => {
                          if (isM365 && m365ReadonlyKeys.has(field.key)) {
                            return;
                          }
                          handleConfigChange(field.key, e.target.value);
                        }}
                        variant="standard"
                        multiline={field.type === "textarea"}
                        rows={field.type === "textarea" ? 5 : 1}
                        type={field.type === "password" ? "password" : field.type === "email" ? "email" : "text"}
                        InputProps={{
                          disableUnderline: true,
                          readOnly:
                            isM365 && m365ReadonlyKeys.has(field.key),
                          endAdornment:
                            isM365 &&
                            field.key === "client_secret" &&
                            m365ClientSecretUpdated ? (
                              <InputAdornment position="end" sx={{ mr: 1 }}>
                                <Chip
                                  size="small"
                                  label="Updated"
                                  sx={styles.updatedChip}
                                />
                              </InputAdornment>
                            ) : isM365 &&
                              field.key === "client_secret" &&
                              m365SecretExpiryChipLabel ? (
                              <InputAdornment position="end" sx={{ mr: 1 }}>
                                <M365ClientSecretExpiryChip
                                  label={m365SecretExpiryChipLabel}
                                  tone={m365SecretExpiryChipTone}
                                />
                              </InputAdornment>
                            ) : undefined,
                          sx:
                            field.type === "textarea"
                              ? {
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  fontFamily: "var(--theo-font-sans)",
                                  color: "#000",
                                }
                              : styles.textFieldInput,
                        }}
                      />
                      {isM365 && field.key === "client_secret" ? (
                        <M365ClientSecretExpiryCaption
                          expiry={m365SecretExpiry}
                          loading={m365SecretExpiryLoading}
                          pendingNewSecret={m365ClientSecretUpdated}
                        />
                      ) : null}
                      {field.helpText && (
                        <Typography
                          sx={{
                            mt: 0.75,
                            color: "#666F8D",
                            fontSize: "12px",
                            fontFamily: "var(--theo-font-sans)",
                            lineHeight: 1.4,
                          }}
                        >
                          {field.helpText}
                        </Typography>
                      )}
                      {errors[field.key] && (
                        <Typography
                          sx={{
                            mt: 0.75,
                            color: "#d32f2f",
                            fontSize: "12px",
                            fontFamily: "var(--theo-font-sans)",
                          }}
                        >
                          {errors[field.key]}
                        </Typography>
                      )}
                    </Box>
                  )
                )
              ) : (
                <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 3 }}>
                  <Typography sx={{ color: "#666F8D", fontSize: "14px" }}>
                    No configuration fields available for this application.
                  </Typography>
                </Box>
              )}
            </Box>
            {isGWorkspace && customerId && application?.id != null && (
              <GWorkspaceOnboardingPanel
                customerId={customerId}
                applicationId={application.id}
                adminEmail={formValues.admin_email || ""}
                startSignal={0}
                manuallyVerified={onboardingFailedOpen && (testSucceededAfterEdit || gworkspaceRescued)}
                onRunChange={setOnboardingRun}
                onConfigurationSaved={handleOnboardingConfigurationSaved}
                onFinished={handleOnboardingFinished}
              />
            )}
            {/*
              What to do about it, under the checklist that shows what happened. Cut per step
              (Q97): only the steps a failed run did not do, or every step for a customer with
              nothing set up. A run that finished needs none, and neither does a customer who is
              already configured.
            */}
            {gworkspaceGuideSteps && (
              <CollapsibleSetupGuidePanel
                contentId="gworkspace-app-config-instructions"
                expanded={gworkspaceInstructionsExpanded}
                onExpandedChange={setGworkspaceInstructionsExpanded}
              >
                <GWorkspaceManualStepInstructions
                  steps={gworkspaceGuideSteps.length ? gworkspaceGuideSteps : undefined}
                  heading={onboardingFailedOpen ? "Finish the setup by hand" : "Setup Guide"}
                />
              </CollapsibleSetupGuidePanel>
            )}
            {isGWorkspace &&
              gworkspaceTestResult?.scope_checks &&
              gworkspaceTestResult.scope_checks.length > 0 && (
                <GWorkspaceOAuthScopeChecksResultsBox
                  checks={gworkspaceTestResult.scope_checks}
                />
              )}
            {isGWorkspace && gworkspaceTestResult?.checks && gworkspaceTestResult.checks.length > 0 && (
              <GWorkspaceConnectionChecksResultsBox checks={gworkspaceTestResult.checks} />
            )}
            {isM365 &&
              m365TestResult?.checks &&
              m365TestResult.checks.length > 0 && (
                <M365ConnectionChecksResultsBox checks={m365TestResult.checks} />
              )}
            <Box
              sx={{
                ...styles.footerContainer,
                // No status chip on the left for 1Password, so Save would otherwise sit there alone.
                ...(isOnePassword && { justifyContent: "flex-end" }),
                ...(isM365 && {
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                  width: "100%",
                }),
              }}
            >
              {(!isM365 || !m365TestResult?.checks?.length) &&
                connectionStatus.show && (
                <Chip
                  icon={
                    <Box
                      component="span"
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: connectionStatus.color,
                        mask: `url(${WarningInfo}) no-repeat center / contain`,
                        WebkitMask: `url(${WarningInfo}) no-repeat center / contain`,
                      }}
                    />
                  }
                  label={connectionStatus.label}
                  sx={styles.connectionChip}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isM365 ? "flex-start" : "flex-end",
                  gap: 1,
                  ...(isM365
                    ? {
                        flex: "1 1 auto",
                        minWidth: 0,
                        width: "100%",
                        maxWidth: "100%",
                      }
                    : {
                        maxWidth: { xs: "100%", sm: "min(100%, 720px)" },
                      }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: isM365 ? "flex-start" : "flex-end",
                    alignItems: isM365 ? "flex-start" : "center",
                    width: isM365 ? "100%" : "auto",
                  }}
                >
                  {!isOnePassword && (
                    <Button
                      variant="contained"
                      sx={{
                        ...styles.testButton,
                        mr: 0,
                        ...(isM365 && {
                          minWidth: "auto",
                          width: "fit-content",
                          maxWidth: "100%",
                          flex: "0 0 auto",
                          px: 2,
                          justifyContent: "center",
                          textAlign: "center",
                        }),
                      }}
                      onClick={handleTestConnection}
                      disabled={
                        testBusy ||
                        isSaving ||
                        m365GrantLoading ||
                        onboardingRunning
                      }
                      startIcon={
                        isM365 ? (
                          testBusy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Box
                              component="span"
                              aria-hidden
                              sx={{
                                display: "inline-flex",
                                width: 22,
                                height: 16,
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            />
                          )
                        ) : testBusy ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {testBusy
                        ? isM365
                          ? "Testing M365 Credentials and Permissions ..."
                          : "Testing..."
                        : isM365
                          ? "Test M365 Credentials and Permissions"
                          : "Test"}
                    </Button>
                  )}
                  {isM365 && (
                    <Button
                      variant="contained"
                      onClick={handleGrantM365ApiPermissions}
                      title={
                        hasUnsavedChanges
                          ? "Test and save the new client secret before granting permissions."
                          : undefined
                      }
                      disabled={
                        m365GrantLoading ||
                        isTesting ||
                        isSaving ||
                        hasUnsavedChanges
                      }
                      startIcon={
                        m365GrantLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Box
                            component="span"
                            aria-hidden
                            sx={{
                              display: "inline-flex",
                              width: 22,
                              height: 16,
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          />
                        )
                      }
                      sx={{
                        ...styles.testButton,
                        mr: 0,
                        minWidth: "auto",
                        width: "fit-content",
                        maxWidth: "100%",
                        flex: "0 0 auto",
                        px: 2,
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {m365GrantLoading
                        ? "Granting permissions…"
                        : "Grant API Permissions"}
                    </Button>
                  )}
                  {/* Never [Start] here: a guided run begins in the Add-Integration modal (Q132). */}
                  {
                    <Button
                      variant="contained"
                      sx={{
                        ...styles.testButton,
                        mr: 0,
                        ...(isM365 && {
                          minWidth: "auto",
                          width: "fit-content",
                          maxWidth: "100%",
                          flex: "0 0 auto",
                          px: 2,
                          justifyContent: "center",
                          textAlign: "center",
                        }),
                      }}
                      onClick={() => {
                        console.log("[AppConfig] Save button clicked");
                        handleSaveConfig();
                      }}
                      disabled={
                        isSaving ||
                        testBusy ||
                        m365GrantLoading ||
                        (isOnePassword ? !hasUnsavedChanges : !testSucceededAfterEdit) ||
                        onboardingRunning
                      }
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                  }
                </Box>
                {testFooterMessage && (
                  <Alert
                    severity={testFooterMessage.severity}
                    onClose={() => setTestFooterMessage(null)}
                    sx={{
                      width: "100%",
                      maxWidth: isM365 ? "100%" : { xs: "100%", sm: "min(100%, 720px)" },
                      alignSelf: isM365 ? "stretch" : "flex-end",
                      fontFamily: "var(--theo-font-sans)",
                    }}
                  >
                    {testFooterMessage.message}
                  </Alert>
                )}
                {isM365 && m365GrantFeedback && (
                  <Alert
                    severity={
                      m365GrantFeedback.success ? "success" : "error"
                    }
                    sx={{
                      width: "100%",
                      fontFamily: "var(--theo-font-sans)",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {m365GrantFeedback.message}
                  </Alert>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Permissions Section */}
      <Paper sx={styles.paperContainer}>
        <Box
          sx={styles.sectionHeader}
          onClick={() => setPermissionsOpen(!permissionsOpen)}
        >
          <Typography sx={styles.sectionTitle}>Permissions</Typography>
          <IconButton size="small" sx={styles.expandButton}>
            {permissionsOpen ? (
              <img src={RemoveIcon} alt="Remove" />
            ) : (
              <img src={AddIcon} alt="Add" />
            )}
          </IconButton>
        </Box>
        {permissionsOpen && (
          <Box sx={{ ...styles.configContent, p: "8px" }}>
            {applicationActionsLoading && <Typography>Loading permissions...</Typography>}
            {error && <Typography color="error">{error}</Typography>}
            {!applicationActionsLoading && !error && (
              <IntegrationTable
                name={application?.name || ""}
                logo={autotaskLogo}
                actions={safeActions}
                enabled={true}
                customerId={customerId || ""}
                applicationId={application?.id || ""}
                onToggle={() => {}}
                onPermissionUpdate={handlePermissionUpdate}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={1500}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppConfiguration;
